import React, { useCallback, useState } from 'react';
import _isUndefined from 'lodash/isUndefined';
import { SEQUENCE_MODAL_TYPE } from '../../../image/labelling/components/frames-control-panel/frames-timeline/modernised-annotation-timeline-modal/constants';
import { SequenceOverrideConfirmationModal } from '../../../image/labelling/components/frames-control-panel/frames-timeline/sequence-override-confirmation-modal';
import type { FrameSpanT } from '../../../image/labelling/reducers/actions';
import { NOTIFICATION_TYPE } from '../../constant/constant';
import useModal from '../../hooks/useModal';
import type { AnswerT, DynamicAnnotationConfigT, GenericAnnotationT } from '../../types/types';
import { dynamicAnnotationConfigFactory, getExclusiveRanges } from '../utils';
type PropsT = {
  getDynamicAnswersRange?: (a: string) => {
    frameStart: number;
    frameEnd: number;
    annotation: GenericAnnotationT;
    isValid: boolean;
    id: string;
    savedToStore: boolean;
  }[];
  selectedAnnotationId: string;
  annotation: GenericAnnotationT;
  resetDynamicFields: (annotationId: string, frameSpan: FrameSpanT) => void;
  //It is required to be optional becuase old side panel.
  getSelectedAnnotationFrameRange?: (annotationId: string) => {
    startFrameIndex: number;
    endFrameIndex: number;
  };
  showNotification: (notificationType: (typeof NOTIFICATION_TYPE)[keyof typeof NOTIFICATION_TYPE], message: string) => void;
  updateDynamicAnnotation: (b: string, a: GenericAnnotationT, range: FrameSpanT) => void;
  editableAnnotationId: string;
  validateDynamicAnnotation: (dynamicAnnotation: GenericAnnotationT) => boolean;
};
const useDynamicQuestionnaire = ({
  selectedAnnotationId,
  annotation,
  editableAnnotationId,
  getDynamicAnswersRange,
  resetDynamicFields,
  getSelectedAnnotationFrameRange,
  showNotification,
  updateDynamicAnnotation,
  validateDynamicAnnotation
}: PropsT) => {
  const {
    openModal
  } = useModal();
  const initialDynamicAnnotationConfig = () => {
    if (getDynamicAnswersRange?.(selectedAnnotationId)?.length) {
      return getDynamicAnswersRange?.(selectedAnnotationId);
    }
    return [dynamicAnnotationConfigFactory(selectedAnnotationId, annotation, getSelectedAnnotationFrameRange)];
  };

  //Start and end is 1 indexed
  const [dynamicAnnotationConfig, setDynamicAnnotationConfig] = useState<DynamicAnnotationConfigT[]>(() => initialDynamicAnnotationConfig());
  const createNewDynamicQuestionnaire = useCallback(() => {
    setDynamicAnnotationConfig(prevDynamicAnnotationConfig => {
      return [...prevDynamicAnnotationConfig, dynamicAnnotationConfigFactory(selectedAnnotationId, annotation, getSelectedAnnotationFrameRange)];
    });
  }, [annotation, getSelectedAnnotationFrameRange, selectedAnnotationId]);
  const resetDynamicAnnotationConfig = useCallback(() => {
    setDynamicAnnotationConfig([]);
  }, []);
  const updateFrameStart = useCallback((id: string, frameStart: number) => {
    const dynamicQuestionnaireIdx = dynamicAnnotationConfig.findIndex(dynamicAnnotationData => dynamicAnnotationData.id === id);
    if (dynamicQuestionnaireIdx === -1) return;
    setDynamicAnnotationConfig(prevDynamicAnnotationConfig => {
      const newDynamicAnnotationConfig = [...prevDynamicAnnotationConfig];
      const selectedDynamicAnnotationConfig = newDynamicAnnotationConfig[dynamicQuestionnaireIdx];
      if (!selectedDynamicAnnotationConfig) {
        console.error('selectedDynamicAnnotationConfig is invalid', dynamicQuestionnaireIdx);
        return newDynamicAnnotationConfig;
      }
      newDynamicAnnotationConfig[dynamicQuestionnaireIdx] = {
        ...selectedDynamicAnnotationConfig,
        frameStart
      };
      return newDynamicAnnotationConfig;
    });
  }, [dynamicAnnotationConfig]);
  const updateFrameEnd = useCallback((id: string, frameEnd: number) => {
    const dynamicQuestionnaireIdx = dynamicAnnotationConfig.findIndex(dynamicAnnotationData => dynamicAnnotationData.id === id);
    if (dynamicQuestionnaireIdx === -1) return;
    setDynamicAnnotationConfig(prevDynamicAnnotationConfig => {
      const newDynamicAnnotationConfig = [...prevDynamicAnnotationConfig];
      const dynamicAnnotationConfig = newDynamicAnnotationConfig[dynamicQuestionnaireIdx];
      if (!dynamicAnnotationConfig) {
        console.error('dynamicQuestionnaireIdx is invalid', dynamicQuestionnaireIdx);
        return newDynamicAnnotationConfig;
      }
      newDynamicAnnotationConfig[dynamicQuestionnaireIdx] = {
        ...dynamicAnnotationConfig,
        frameEnd
      };
      return newDynamicAnnotationConfig;
    });
  }, [dynamicAnnotationConfig]);
  const handleOnDynamicQuestionnaireChange = useCallback((id: string, isAnswerValid: boolean, updatedAnswers?: AnswerT) => {
    const dynamicQuestionnaireIdx = dynamicAnnotationConfig.findIndex(dynamicAnnotationData => dynamicAnnotationData.id === id);
    if (dynamicQuestionnaireIdx === -1) return;
    setDynamicAnnotationConfig(prevDynamicAnnotationConfig => {
      const newDynamicAnnotationConfig = [...prevDynamicAnnotationConfig];
      const dynamicAnnotationConfig = newDynamicAnnotationConfig[dynamicQuestionnaireIdx];
      if (!dynamicAnnotationConfig) {
        console.error('dynamicQuestionnaireIdx is invalid', dynamicQuestionnaireIdx);
        return newDynamicAnnotationConfig;
      }
      newDynamicAnnotationConfig[dynamicQuestionnaireIdx] = {
        ...dynamicAnnotationConfig,
        isValid: isAnswerValid,
        annotation: {
          ...dynamicAnnotationConfig.annotation,
          metadata: {
            ...(dynamicAnnotationConfig.annotation.metadata || {}),
            answers: updatedAnswers
          }
        }
      };
      return newDynamicAnnotationConfig;
    });
  }, [dynamicAnnotationConfig]);
  const onRemoveDynamicAttribute = useCallback((id: string) => {
    const annotationConfigIdx = dynamicAnnotationConfig.findIndex(data => data.id === id);
    const selectedDynamicAnnotationConfig = dynamicAnnotationConfig[annotationConfigIdx];
    if (!selectedDynamicAnnotationConfig) {
      console.error('selectedDynamicAnnotationConfig is invalid', annotationConfigIdx);
      return;
    }
    if (annotationConfigIdx !== -1 && selectedDynamicAnnotationConfig.savedToStore) {
      resetDynamicFields(selectedAnnotationId, {
        startFrame: selectedDynamicAnnotationConfig.frameStart - 1,
        endFrame: selectedDynamicAnnotationConfig.frameEnd - 1
      });
    }
    setDynamicAnnotationConfig(prevDynamicAnnotationsData => {
      return prevDynamicAnnotationsData.filter(dynamicAnnotationData => dynamicAnnotationData.id !== id);
    });
  }, [dynamicAnnotationConfig, resetDynamicFields, selectedAnnotationId]);
  const getConflictingRanges = useCallback((index: number) => {
    const currentDynamicAnnotationConfig = dynamicAnnotationConfig[index];
    if (!currentDynamicAnnotationConfig) {
      console.error('currentDynamicAnnotationConfig is invalid', index);
      return [];
    }
    const {
      frameEnd,
      frameStart
    } = currentDynamicAnnotationConfig;
    const eligibleRanges = dynamicAnnotationConfig.filter((annotationConfig, idx) => idx !== index && annotationConfig.savedToStore);
    const conflictingRanges = eligibleRanges.filter(annotationConfig => !(annotationConfig.frameStart > frameEnd || annotationConfig.frameEnd < frameStart));
    return conflictingRanges;
  }, [dynamicAnnotationConfig]);
  const onSaveDynamicAttribute = useCallback((id: string) => {
    const dynamicQuestionnaireIdx = dynamicAnnotationConfig.findIndex(dynamicAnnotationData => dynamicAnnotationData.id === id);
    if (dynamicQuestionnaireIdx === -1) return;
    const selectedDynamicAnnotationConfig = dynamicAnnotationConfig[dynamicQuestionnaireIdx];
    if (!selectedDynamicAnnotationConfig) {
      console.error('selectedDynamicAnnotationConfig is invalid', dynamicQuestionnaireIdx);
      return;
    }
    const {
      frameEnd,
      frameStart,
      annotation: dynamicAnnotation
    } = selectedDynamicAnnotationConfig;
    const {
      startFrameIndex,
      endFrameIndex
    } = (annotation ? getSelectedAnnotationFrameRange?.(annotation.id) : undefined) || {};
    if (_isUndefined(startFrameIndex) || _isUndefined(endFrameIndex) || frameEnd - 1 > endFrameIndex || frameStart > frameEnd || frameStart - 1 < startFrameIndex) {
      showNotification(NOTIFICATION_TYPE.ERROR, 'Please fill the frame range correctly.');
      return;
    }
    const conflictingRanges = getConflictingRanges(dynamicQuestionnaireIdx);
    const isValid = validateDynamicAnnotation(dynamicAnnotation);
    if (!isValid) {
      showNotification(NOTIFICATION_TYPE.ERROR, 'Please fill the dynamic questionnaire answers correctly.');
      return;
    }
    const hasConflictingRanges = conflictingRanges.length;
    const onConfirm = () => {
      updateDynamicAnnotation(editableAnnotationId, dynamicAnnotation, {
        startFrame: frameStart - 1,
        endFrame: frameEnd - 1
      });
      setDynamicAnnotationConfig(prevConfig => {
        const clonedConfig = [...prevConfig];
        const conflictingRangesIds = conflictingRanges.map(range => range.id);
        const selectedClonedConfig = clonedConfig[dynamicQuestionnaireIdx];
        if (!selectedClonedConfig) {
          console.error('selectedClonedConfig is invalid', dynamicQuestionnaireIdx);
          return clonedConfig;
        }
        selectedClonedConfig.savedToStore = true;
        const finalRanges = clonedConfig.reduce((acc, currentConfig) => {
          if (conflictingRangesIds.includes(currentConfig.id)) {
            const exclusiveRanges = getExclusiveRanges(currentConfig, selectedDynamicAnnotationConfig);
            return [...acc, ...exclusiveRanges];
          }
          return [...acc, currentConfig];
        }, []);
        return finalRanges;
      });
      showNotification(NOTIFICATION_TYPE.SUCCESS, 'Dynamic Questionnaire Saved');
    };
    if (hasConflictingRanges) {
      openModal({
        ModalComponent: props => <SequenceOverrideConfirmationModal {...props} onConfirm={() => {
          onConfirm();
          props.closeModal();
        }} onCancel={() => {
          props.closeModal();
        }} isOpen={props.isOpen}
        // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
        startFrame={conflictingRanges[0].frameStart - 1}
        // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
        endFrame={conflictingRanges[0].frameEnd - 1} labelTitle={annotation?.annotationLabel?.title ?? ''} type={SEQUENCE_MODAL_TYPE.TRACK} />
      });
    } else {
      onConfirm();
    }
  }, [annotation, dynamicAnnotationConfig, editableAnnotationId, getConflictingRanges, getSelectedAnnotationFrameRange, openModal, showNotification, updateDynamicAnnotation, validateDynamicAnnotation]);
  return {
    createNewDynamicQuestionnaire,
    resetDynamicAnnotationConfig,
    updateFrameStart,
    updateFrameEnd,
    handleOnDynamicQuestionnaireChange,
    onRemoveDynamicAttribute,
    onSaveDynamicAttribute,
    dynamicAnnotationConfig
  };
};
export default useDynamicQuestionnaire;